"use client";

import { useMemo } from "react";
import type { Member, PermissionAssetType, ShareRole } from "@zapier/bff";
import { AddShareForm, ShareList } from "@zapier/shared";
import { useCreateAssetShare, useSplitTreatments } from "@zapier/client";
import { useNotification } from "@zapier/utils";

import type { AddShareFormProps } from "@zapier/shared";

import styles from "./TemplateShareTabContent.module.css";
import { formatTemplateShareErrorMessage } from "./templateShareErrorMessage";

type OnShareParameters = Parameters<
  NonNullable<AddShareFormProps["onShare"]>
>[0];

export interface TemplateShareTabContentProps {
  templateId: string;
  templateName: string;
  /** Owner of the template; used for owner row and to disable sharing with self. */
  assetOwner?: Member;
  /** When false, add-share form is hidden and share list is read-only (e.g. user does not own template). */
  canShare?: boolean;
  /** Search members/teams on this account (parent org id on the Organization tab when using a workspace account). */
  memberSearchAccountId?: string;
  /** Labels "Everyone at …" for the org scope (parent org when on the Organization tab under a workspace). */
  everyoneOptionAccount?: {
    id: number;
    name?: string | null;
    logoUrl?: string | null;
  };
}

export function TemplateShareTabContent({
  templateId,
  templateName,
  assetOwner,
  canShare = true,
  memberSearchAccountId,
  everyoneOptionAccount,
}: TemplateShareTabContentProps) {
  const { createNotification } = useNotification();
  const { mutateAsync: createShare } = useCreateAssetShare();
  const { isEditorZapTemplatesEditModeEnabled } = useSplitTreatments();

  const roles = useMemo<ShareRole[]>(
    () =>
      isEditorZapTemplatesEditModeEnabled ? ["viewer", "editor"] : ["viewer"],
    [isEditorZapTemplatesEditModeEnabled],
  );

  const handleOnShare = async ({ role, subject }: OnShareParameters) => {
    try {
      await createShare({
        assetId: templateId,
        assetType: "template",
        subjectId: subject.id,
        subjectType: subject.type,
        role,
      });
      createNotification({
        type: "success",
        message: `Shared "${templateName}" with ${subject.name}.`,
      });
    } catch (error) {
      createNotification({
        type: "error",
        message: formatTemplateShareErrorMessage(
          error,
          templateName,
          subject.name,
          Boolean(memberSearchAccountId),
        ),
      });
    }
  };

  return (
    <div className={styles.contentWrapper}>
      {canShare && (
        <AddShareForm
          allowSharingWithEveryone={true}
          allowSharingWithAdmins={false}
          allowSharingWithTeams={true}
          assetId={templateId}
          assetType={"template" as PermissionAssetType}
          disabledUserIds={assetOwner?.id ? [String(assetOwner.id)] : undefined}
          label="Add member or team"
          roles={roles}
          searchAccountId={memberSearchAccountId}
          everyoneOptionAccount={everyoneOptionAccount}
          onShare={handleOnShare}
        />
      )}

      <div className={styles.shareList}>
        <ShareList
          isDisabled={!canShare}
          assetId={templateId}
          assetOwner={assetOwner}
          assetType={"template" as PermissionAssetType}
          roles={roles}
        />
      </div>
    </div>
  );
}
